import React, { useCallback, useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Card } from "../ui/card";
import { Button } from "../ui/button";
import { AlertTriangle, Download, FileSignature, Loader2, UserPlus } from "lucide-react";
import { toast } from "sonner";
import { ParticipanteCard } from "./ParticipanteCard";
import { AssinarModal } from "./AssinarModal";
import { STATUS_GERAL, fmtBRL } from "./assinaturasConst";

const baixar = async (url, nome) => {
  try {
    const { data } = await api.get(url, { responseType: "blob" });
    const href = window.URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
    const a = document.createElement("a");
    a.href = href;
    a.download = nome;
    a.click();
    window.URL.revokeObjectURL(href);
  } catch {
    toast.error("Não foi possível baixar o PDF");
  }
};

export const PropostaAssinaturasCard = ({ proposta }) => {
  const base = `/propostas/${proposta.id}/assinaturas`;
  const [rec, setRec] = useState(null);
  const [assinando, setAssinando] = useState(null);
  const [ocupado, setOcupado] = useState(false);

  const carregar = useCallback(async () => {
    const { data } = await api.get(base);
    setRec(data);
  }, [base]);

  useEffect(() => { carregar().catch(() => setRec({ iniciada: false })); }, [carregar]);

  const acao = async (fn, ok) => {
    setOcupado(true);
    try {
      const { data } = await fn();
      if (data?.signatarios) setRec({ iniciada: true, ...data });
      else await carregar();
      if (ok) toast.success(ok);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Operação não concluída");
    } finally {
      setOcupado(false);
    }
  };

  const copiarLink = async (p) => {
    try {
      const { data } = await api.post(`${base}/${p.id}/gerar-link`);
      const url = `${window.location.origin}${data.path}`;
      await navigator.clipboard?.writeText(url).catch(() => {});
      toast.success("Link copiado. Válido por 30 dias", { description: url });
      await carregar();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Não foi possível gerar o link");
    }
  };

  const st = STATUS_GERAL[rec?.status_geral] || STATUS_GERAL.pendente;
  const tid = `assinaturas-proposta-${proposta.numero || proposta.id}`;

  return (
    <Card className="p-5 space-y-4" data-testid={tid}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="font-display font-bold text-lg" style={{ color: "#1B3A6B" }}>Proposta {proposta.numero}</div>
          <div className="text-sm text-muted-foreground">{proposta.cliente_nome} · {fmtBRL(proposta.valor_total_geral)}</div>
        </div>
        <span className="rounded-md px-2.5 py-1 text-xs font-semibold" style={{ color: st.color, background: st.bg }} data-testid={`${tid}-status`}>
          {st.label}
        </span>
      </div>

      {!rec ? (
        <Loader2 className="animate-spin text-muted-foreground" size={18} />
      ) : !rec.iniciada ? (
        <Button onClick={() => acao(() => api.post(`${base}/iniciar`, {}), "Assinaturas iniciadas")} disabled={ocupado} className="text-white" style={{ background: "#1B3A6B" }} data-testid={`${tid}-iniciar`}>
          <FileSignature size={14} className="mr-1" /> Iniciar assinaturas
        </Button>
      ) : (
        <>
          {!rec.representante_configurado && (
            <div className="flex items-center gap-2 text-xs rounded-md bg-amber-50 text-amber-800 px-3 py-2" data-testid={`${tid}-aviso-representante`}>
              <AlertTriangle size={14} /> Configure o representante padrão em Cadastros → Unidades
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {[...rec.signatarios, ...rec.testemunhas].map((p) => (
              <ParticipanteCard
                key={p.id}
                p={p}
                onAssinar={setAssinando}
                onLink={copiarLink}
                onSalvar={(pp, f) => acao(() => api.put(`${base}/participantes/${pp.id}`, f), "Dados atualizados")}
                onRemover={p.papel.startsWith("testemunha") ? (pp) => acao(() => api.delete(`${base}/testemunhas/${pp.id}`), "Testemunha removida") : null}
              />
            ))}
          </div>
          {rec.status_geral !== "completa" && rec.testemunhas.length < 2 && (
            <Button size="sm" variant="outline" disabled={ocupado} onClick={() => acao(() => api.post(`${base}/testemunhas`, {}), "Testemunha adicionada")} data-testid={`${tid}-add-testemunha`}>
              <UserPlus size={13} className="mr-1" /> Adicionar testemunha
            </Button>
          )}
          <div className="border-t pt-3 flex flex-wrap gap-2">
            <Button size="sm" variant="outline" onClick={() => baixar(`/proposta-nativa/${proposta.id}/pdf`, `Proposta_${proposta.numero}.pdf`)} data-testid={`${tid}-pdf-original`}>
              <Download size={13} className="mr-1" /> Baixar PDF para envio manual
            </Button>
            {rec.status_geral === "completa" && (
              <Button size="sm" className="text-white" style={{ background: "#15803d" }} onClick={() => baixar(`${base}/pdf`, `Proposta_${proposta.numero}_assinada.pdf`)} data-testid={`${tid}-pdf-assinado`}>
                <Download size={13} className="mr-1" /> Baixar PDF com assinaturas
              </Button>
            )}
          </div>
        </>
      )}
      <AssinarModal
        participante={assinando}
        onClose={() => setAssinando(null)}
        onConfirm={async (payload) => {
          const { data } = await api.post(`${base}/${assinando.id}/assinar-presencial`, payload);
          setRec({ iniciada: true, ...data });
          toast.success(data.status_geral === "completa" ? "Assinaturas concluídas. Proposta assinada" : "Assinatura registrada");
        }}
      />
    </Card>
  );
};
